import Btc from '@ledgerhq/hw-app-btc'
import { getAppAndVersion } from '@ledgerhq/hw-app-btc/getAppAndVersion'
import Eth, { ledgerService } from '@ledgerhq/hw-app-eth'
import * as Transport from '@ledgerhq/hw-transport'
import TransportWebUSB from '@ledgerhq/hw-transport-webusb'
import { bitcoinjs } from '@optimex-xyz/btc-script'
import {
  broadcastTx,
  getBtcFeeRate,
  getConfirmedUtxos,
  getProvider,
  getUtxoHex,
  selectUtxos,
} from '@optimex-xyz/trade-wallet-sdk'

import { INetwork } from '@/types'
import { isBtcChain, isEvmChain } from '@/utils'
import { SUPPORTED_NETWORK, WALLET_LEDGER } from '@/utils/constants'

import { ethers } from 'ethers'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, PsbtSignOptions, TransactionLedger, TransactionOptions } from './types'

const USB_INTERFACE_TIMEOUT = 5000

function getFormat(derivationPath: string) {
  return derivationPath.startsWith("m/84'") || derivationPath.startsWith("m/86'")
    ? 'bech32' // Native SegWit
    : derivationPath.startsWith("m/49'")
      ? 'p2sh' // P2SH-SegWit
      : 'legacy' // P2PKH
}

function amountToBuffer(amount: number): Buffer {
  const buffer = Buffer.alloc(8)
  buffer.writeBigUInt64LE(BigInt(amount))
  return buffer
}

export class LedgerWallet extends BaseWallet {
  name = 'Ledger Wallet'
  key = WALLET_LEDGER

  private transport: TransportWebUSB | null = null
  private ethApp: Eth | null = null
  private btcApp: Btc | null = null
  private provider: ethers.JsonRpcProvider | undefined
  private connectedAddresses: Record<string, string> = {}
  private path: string = ''

  private getNetworkParams(network: string, path: string): { path: string; coinType: string } {
    if (isEvmChain(network)) {
      return { path, coinType: 'ethereum' }
    }
    switch (network) {
      case SUPPORTED_NETWORK.BTC:
        return { path, coinType: 'bitcoin' }
      case SUPPORTED_NETWORK.BTC_TESTNET:
        return { path, coinType: 'bitcoin_testnet' }
      default:
        throw new Error(`Unsupported network: ${network}`)
    }
  }

  private async initTransport(): Promise<TransportWebUSB> {
    try {
      if (this.transport) {
        try {
          await this.transport.close()
        } catch (e) {
          console.warn('Error closing transport:', e)
        }
        this.transport = null
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }

      await TransportWebUSB.request()
      const transport = (await TransportWebUSB.create(undefined, USB_INTERFACE_TIMEOUT)) as TransportWebUSB
      transport.setExchangeTimeout(30000)
      this.transport = transport
      return transport
    } catch (error: unknown) {
      if (error instanceof Error && error.message?.includes('Unable to claim interface')) {
        throw new Error(
          'Ledger is in use by another application. Please close other applications (like Ledger Live) and try again.'
        )
      }
      if (error instanceof Error) {
        throw new Error(error.message)
      }
      throw new Error('An unknown error occurred while initializing transport')
    }
  }

  private async getEthApp(): Promise<Eth> {
    if (!this.ethApp) {
      const transport = await this.initTransport()
      try {
        this.ethApp = new Eth(transport)
        await this.ethApp.getAppConfiguration()
      } catch (error: unknown) {
        this.ethApp = null
        if (error instanceof Error) {
          throw new Error(`Please open the Ethereum app on your Ledger device: ${error.message}`)
        }
        throw new Error('Please open the Ethereum app on your Ledger device')
      }
    }
    return this.ethApp
  }

  private async getBtcApp(networkId: string): Promise<Btc> {
    if (!this.btcApp) {
      const transport = await this.initTransport()
      try {
        await transport.send(0xb0, 0x01, 0x00, 0x00)
      } catch (error: unknown) {
        if (error instanceof Error) {
          throw new Error(`Please open the Bitcoin app on your Ledger device: ${error.message}`)
        }
        throw new Error('Please open the Bitcoin app on your Ledger device')
      }

      this.btcApp = new Btc({
        transport: transport as unknown as Transport.default,
        currency: networkId === SUPPORTED_NETWORK.BTC ? SUPPORTED_NETWORK.BTC : SUPPORTED_NETWORK.BTC_TESTNET,
      })
    }
    return this.btcApp
  }

  async connect(
    network: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    if (!options.path) throw new Error('Path is required')
    const { path: defaultPath, coinType } = this.getNetworkParams(network, options.path)
    this.path = options?.path || defaultPath

    let address: string
    let compressedPublicKey: string | undefined

    if (coinType === 'ethereum') {
      this.provider = getProvider(network)
      const ethApp = await this.getEthApp()
      const result = await ethApp.getAddress(this.path)
      address = result.address
      compressedPublicKey = result.publicKey
    } else {
      const btcApp = await this.getBtcApp(network)

      const result = await btcApp.getWalletPublicKey(this.path, {
        format: getFormat(this.path),
      })
      address = result.bitcoinAddress
      compressedPublicKey = result.publicKey
    }

    this.connectedAddresses[network] = address
    return { address, networkInfo: options.network, compressedPublicKey }
  }

  async disconnect(): Promise<void> {
    if (this.transport) {
      await this.transport.close()
      this.transport = null
    }
    this.ethApp = null
    this.btcApp = null
    this.connectedAddresses = {}
  }

  async getAddress(network: string): Promise<string> {
    return this.connectedAddresses[network]
  }

  async sendBitcoinTransaction(
    networkId: string,
    btcApp: Btc,
    fromAddress: string,
    toAddress: string,
    amount: number,
    derivationPath: string,
    options?: TransactionOptions
  ): Promise<string> {
    const feeRate = options?.feeRate || (await getBtcFeeRate(networkId))
    // Add 10% to make it better rate
    const confirmedUtxos = await getConfirmedUtxos(networkId, fromAddress)
    const { selectedUtxos, totalValue, estimatedFee } = await selectUtxos(
      confirmedUtxos,
      derivationPath,
      fromAddress,
      toAddress,
      amount,
      feeRate,
      options?.feeRateMultiplier
    )

    const changeAmount = totalValue - amount - estimatedFee

    const format = getFormat(derivationPath)

    const inputs: [TransactionLedger, number, string | null | undefined, number | null | undefined][] = []

    const associatedKeysets = []

    for (const utxo of selectedUtxos) {
      const rawTx = await getUtxoHex(utxo.txid, networkId)
      const tx = btcApp.splitTransaction(rawTx, format !== 'legacy', false)
      inputs.push([tx, utxo.vout, undefined, undefined])
      associatedKeysets.push(derivationPath)
    }

    const { version } = await getAppAndVersion(this.transport as TransportWebUSB)
    const transaction = {
      version: Buffer.from(version),
      inputs: [],
      outputs: [
        {
          amount: amountToBuffer(amount),
          script: Buffer.from(
            bitcoinjs.address.toOutputScript(
              toAddress,
              networkId === SUPPORTED_NETWORK.BTC ? bitcoinjs.networks.bitcoin : bitcoinjs.networks.testnet
            )
          ),
        },
      ],
    }

    if (changeAmount > 546) {
      transaction.outputs.push({
        amount: amountToBuffer(changeAmount),
        script: Buffer.from(
          bitcoinjs.address.toOutputScript(
            fromAddress,
            networkId === SUPPORTED_NETWORK.BTC ? bitcoinjs.networks.bitcoin : bitcoinjs.networks.testnet
          )
        ),
      })
    }

    const outputScriptHex = btcApp.serializeTransactionOutputs(transaction).toString('hex')

    const signedTx = await btcApp.createPaymentTransaction({
      inputs,
      associatedKeysets,
      outputScriptHex,
      additionals: format === 'bech32' ? ['bech32'] : [],
      segwit: format !== 'legacy',
    })

    return await broadcastTx(networkId, signedTx)
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    network: string,
    options?: TransactionOptions
  ): Promise<string> {
    if (isEvmChain(network)) {
      if (!this.provider) {
        throw new Error('Provider notfound')
      }
      const ethApp = await this.getEthApp()
      const path = this.getNetworkParams(network, this.path).path
      const address = this.connectedAddresses[network]

      if (!address) {
        throw new Error('Wallet not connected')
      }

      const [nonce, feeData, networkInfo] = await Promise.all([
        this.provider.getTransactionCount(address),
        this.provider.getFeeData(),
        this.provider.getNetwork(),
      ])

      const txRequest = {
        to: toAddress,
        nonce: nonce,
        gasLimit: BigInt(options?.gasLimit || '300000'),
        chainId: Number(networkInfo.chainId),
        value: amount.toString(),
        data: options?.data || '0x',
      }

      if (options?.maxFeePerGas && options?.maxPriorityFeePerGas) {
        Object.assign(txRequest, {
          type: 2,
          maxFeePerGas: options.maxFeePerGas.toString(),
          maxPriorityFeePerGas: options.maxPriorityFeePerGas.toString(),
        })
      } else {
        Object.assign(txRequest, {
          type: 0,
          gasPrice: options?.feeRate || feeData.gasPrice?.toString() || '0',
        })
      }

      const serializedTx = ethers.Transaction.from(txRequest).unsignedSerialized
      const resolution = await ledgerService.resolveTransaction(serializedTx.slice(2), {}, {})
      const signature = await ethApp.signTransaction(path, serializedTx.slice(2), resolution)
      const signedTx = ethers.Transaction.from({
        ...txRequest,
        signature: {
          r: `0x${signature.r}`,
          s: `0x${signature.s}`,
          v: BigInt(parseInt(signature.v, 16)),
        },
      })

      const tx = await this.provider.broadcastTransaction(signedTx.serialized)
      return tx.hash
    }
    if (isBtcChain(network)) {
      try {
        const btcApp = await this.getBtcApp(network)
        const fromAddress = this.connectedAddresses[network]

        if (!fromAddress) {
          throw new Error('Wallet not connected')
        }

        return await this.sendBitcoinTransaction(
          network,
          btcApp,
          fromAddress,
          toAddress,
          Number(amount),
          this.path,
          options
        )
      } catch (error) {
        if (error instanceof Error) {
          throw new Error(`Failed to send BTC transaction: ${error.message}`)
        }
        throw new Error('Failed to send BTC transaction: Unknown error')
      }
    }
    throw new Error(`Unsupported network for sending transaction: ${network}`)
  }

  async signMessage(message: string, network: string): Promise<string> {
    if (!network) {
      throw new Error('Network must be specified for signing messages')
    }

    const { path, coinType } = this.getNetworkParams(network, this.path)

    if (coinType === 'ethereum') {
      const ethApp = await this.getEthApp()
      const msgHex = ethers.hexlify(ethers.toUtf8Bytes(message))
      const result = await ethApp.signPersonalMessage(path, msgHex.slice(2))

      const vNum = result['v'] - 27
      let vHex = vNum.toString(16)
      if (vHex.length < 2) {
        vHex = '0' + vHex
      }

      return '0x' + result['r'] + result['s'] + vHex
    } else {
      const btcApp = await this.getBtcApp(network)
      const result = await btcApp.signMessage(this.path, Buffer.from(message).toString('hex'))
      const v = result['v'] + 27 + 4
      return Buffer.from(v.toString(16) + result['r'] + result['s'], 'hex').toString('base64')
    }
  }

  async getCompressedPublicKey(network: string): Promise<string> {
    if (!network) {
      throw new Error('Network must be specified for getting compressed public key')
    }

    const { coinType } = this.getNetworkParams(network, this.path)

    if (coinType === 'ethereum') {
      return this.connectedAddresses[network]
    } else {
      const btcApp = await this.getBtcApp(network)

      const result = await btcApp.getWalletPublicKey(this.path, {
        format: getFormat(this.path),
      })
      return result.publicKey
    }
  }

  async signPsbt(_psbt: string, _network: string, _options?: PsbtSignOptions): Promise<string> {
    throw new Error('SignPsbt is not supported for Ledger Wallet')
  }

  isInstalled(): boolean {
    return true
  }

  priority(): number {
    return this.isInstalled() ? 0 : 1
  }
}
